import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useCart } from '../contexts/CartContext';
import { useInventory } from '../contexts/InventoryContext';
import { useAuth } from '../contexts/AuthContext';
import { buildOrderItems } from '../utils/orderUtils';
import { buildShippingAddressFromCheckout } from '../utils/addressUtils';
import { notifyPurchase } from '../services/notifyApi';
import { completeOrder } from '../services/ordersApi';
import './SuccessPage.css';

const base = process.env.REACT_APP_API_URL || '';

const verifyAndGetSession = async (sessionId) => {
  try {
    const res = await fetch(`${base}/api/verify-session/${encodeURIComponent(sessionId)}`);
    if (!res.ok) return null;
    return res.json();
  } catch (err) {
    console.error('Erro ao verificar sessão:', err);
    return null;
  }
};

const getSessionDetails = async (sessionId) => {
  try {
    const res = await fetch(`${base}/api/session-details/${encodeURIComponent(sessionId)}`);
    if (!res.ok) return null;
    return res.json();
  } catch (err) {
    console.error('Erro ao buscar detalhes da sessão:', err);
    return null;
  }
};

const saveOrderClientFallback = async (sessionId, items, sessionDetails, userId, localShipping) => {
  if (items.length === 0) {
    console.warn('Pedido sem itens — fallback cliente não executado');
    return false;
  }

  try {
    await setDoc(doc(db, 'orders', sessionId), {
      sessionId,
      userId: userId || null,
      customerEmail: (sessionDetails?.customerEmail || '').trim().toLowerCase() || null,
      customerName: sessionDetails?.customerName || null,
      customerPhone: sessionDetails?.customerPhone || null,
      shippingAddress: sessionDetails?.shippingAddress || null,
      shippingService: localShipping?.label || sessionDetails?.shippingService || null,
      shippingCost: localShipping?.localDelivery ? 0 : (sessionDetails?.shippingCost || 0),
      localDelivery: Boolean(localShipping?.localDelivery),
      items,
      total: sessionDetails?.total || 0,
      status: 'pago',
      createdAt: serverTimestamp(),
    }, { merge: true });
    return true;
  } catch (err) {
    console.error('Erro ao salvar pedido (fallback cliente):', err);
    return false;
  }
};

const SuccessPage = () => {
  const navigate = useNavigate();
  const searchParams = useSearchParams()[0];
  const { clearCart } = useCart();
  const { decrementStock } = useInventory();
  const { currentUser } = useAuth();
  const sessionId = searchParams.get('session_id');
  const processed = useRef(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (!sessionId || processed.current) return;
    processed.current = true;

    (async () => {
      let shouldClearCart = false;

      try {
        const verified = await verifyAndGetSession(sessionId);
        if (!verified?.paid) {
          console.warn('Pagamento não confirmado');
          return;
        }

        shouldClearCart = true;

        const pendingCart = sessionStorage.getItem('haka_pending_cart');
        const pendingShipping = sessionStorage.getItem('haka_pending_shipping');
        let cartItems = [];
        let localShipping = null;

        if (pendingShipping) {
          try { localShipping = JSON.parse(pendingShipping); } catch (_) { }
          sessionStorage.removeItem('haka_pending_shipping');
        }

        if (pendingCart) {
          try { cartItems = JSON.parse(pendingCart); } catch (_) { }
          sessionStorage.removeItem('haka_pending_cart');
        }

        let saved = false;
        let items = [];

        try {
          const idToken = currentUser ? await currentUser.getIdToken() : null;
          const result = await completeOrder({
            sessionId,
            idToken,
            cartItems,
            localShipping,
          });
          saved = result.saved || result.alreadyExists;
          items = result.items || [];
        } catch (apiErr) {
          console.warn('Salvamento via API falhou, tentando fallback:', apiErr.message);

          const details = await getSessionDetails(sessionId);
          if (!details) {
            setSaveError('Pagamento confirmado, mas não foi possível registrar o pedido. Entre em contato conosco.');
            return;
          }

          if (localShipping) {
            details.shippingAddress = buildShippingAddressFromCheckout(
              localShipping,
              details.shippingAddress
            );
            if (localShipping.phone) {
              details.customerPhone = localShipping.phone.trim();
            }
            if (!details.shippingService && localShipping.label) {
              details.shippingService = localShipping.label;
            }
          }

          items = buildOrderItems(details, cartItems);
          saved = await saveOrderClientFallback(
            sessionId,
            items,
            details,
            currentUser?.uid,
            localShipping
          );
        }

        if (saved) {
          if (items.length === 0) {
            const details = await getSessionDetails(sessionId);
            items = buildOrderItems(details || {}, cartItems);
          }

          const itemsToDecrement = items
            .filter((item) => item.productId && item.size)
            .map((item) => ({
              productId: item.productId,
              size: item.size,
              quantity: item.quantity || 1,
            }));

          if (itemsToDecrement.length > 0) {
            try {
              await decrementStock(itemsToDecrement);
            } catch (err) {
              console.error('Erro ao decrementar estoque:', err);
            }
          }

          try {
            await notifyPurchase(sessionId);
          } catch (err) {
            console.error('Erro ao enviar email de compra:', err);
          }
        } else {
          setSaveError('Pagamento confirmado, mas houve um problema ao registrar o pedido. Entre em contato conosco.');
        }
      } catch (err) {
        console.error('Erro ao processar pedido:', err);
        setSaveError('Erro ao processar seu pedido. Entre em contato conosco.');
      } finally {
        if (shouldClearCart) {
          clearCart();
        }
      }
    })();
  }, [sessionId, clearCart, decrementStock, currentUser]);

  return (
    <div className="success-page">
      <div className="success-container">
        <div className="success-icon">✓</div>
        <h1 className="success-title">Compra Realizada com Sucesso!</h1>
        <p className="success-message">
          Obrigado pela sua compra!
          {saveError ? (
            <> {saveError}</>
          ) : (
            <>
              {' '}Enviamos um email de confirmação para você.
              Você também pode acompanhar o pedido em <strong>Meus Pedidos</strong>.
            </>
          )}
        </p>
        <div className="success-actions">
          <button onClick={() => navigate('/meus-pedidos')} className="btn btn-filled">
            <span>Ver meus pedidos</span>
          </button>
          <button onClick={() => navigate('/')} className="btn btn-outline">
            <span>Voltar para Home</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SuccessPage;
